/**
 * =========================================================================================
 *     DOBLER DATA SOLUTIONS, LLC - PROPRIETARY AND CONFIDENTIAL
 * =========================================================================================
 * The source code contained herein is owned by Dobler Data Solutions, LLC. The source code
 * and all other copyrightable materials related thereto, including without limitation, all
 * text, logos, designs, graphics, other files, and other code, is protected under Title 17
 * of the United States Code. Any use, including any commercial use, distribution,
 * republication, or display of the source code or any other copyrightable materials related
 * thereto is strictly prohibited. Copyright © 2026 Dobler Data Solutions, LLC.
 * ALL RIGHTS RESERVED.
 * =========================================================================================
 *
 *     File Name       :   useShellStore.ts
 *     Description     :   Zustand store for shell UI state — active route, sidebar visibility, and authenticated caller.
 *     Module          :   005-web-shell
 *     Component Type  :   Store
 *
 * -----------------------------------------------------------------------------------------
 *     CHANGE HISTORY
 * -----------------------------------------------------------------------------------------
 *     Version     Date            Author              Description
 * -----------------------------------------------------------------------------------------
 *     1.0         2026-06-23      Peter Dobler        Initial creation
 * -----------------------------------------------------------------------------------------
 *
 *     DEPENDENCIES
 * -----------------------------------------------------------------------------------------
 *     External        :   zustand
 *     Internal        :   (none)
 *
 *     EXPORTS
 * -----------------------------------------------------------------------------------------
 *     useShellStore AuthCaller
 *
 * =========================================================================================
 */

import { create } from "zustand";

/** The authenticated caller's display identity (mirrors AuthCaller in AuthProvider). */
export interface AuthCaller {
  name: string;
  email: string;
  subject: string;
}

interface ShellState {
  /** The currently active route path. */
  activeRoute: string;
  setActiveRoute: (route: string) => void;

  /** Whether the navigation sidebar is open. */
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;

  /** Authenticated caller identity; null when signed out. */
  caller: AuthCaller | null;
  setCaller: (caller: AuthCaller) => void;
  clearCaller: () => void;
}

export const useShellStore = create<ShellState>()((set) => ({
  activeRoute: "/",
  setActiveRoute: (route) => set({ activeRoute: route }),

  sidebarOpen: true,
  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),

  caller: null,
  setCaller: (caller) => set({ caller }),
  clearCaller: () => set({ caller: null }),
}));
